import { useTranslation } from "react-i18next";
import { parseDateToLgComun4 } from "../../helpers/dateTransform";
import { AvatarGroup } from "../../components/MainComponents/Avatar/AvatarGroup";
import {
  alpha,
  Box,
  darken,
  Grid2,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Slide,
  Stack,
  Tooltip,
  Typography,
  useTheme,
  Zoom,
} from "@mui/material";
import { EGIconButton } from "../MainComponents/Button/IconButton/IconButton";
import { EGButton } from "../MainComponents/Button/EGButton/EGButton";
import { useReportContext } from "../Report/ReportContext/useReportContext";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { IndicatorTags } from "./IndicatorTags/IndicatorTags";
import { catalogByPart } from "../../apis";
import { useSnackbar } from "notistack";
import Icon from "../Icon/Icon";
import useLocalizedLabel from "../../hooks/useLocalizedLabel";
import { TagsPreview } from "../MainComponents/Indicators/TagsPreview";
import { UTCHint } from "../MainComponents";

const HeaderButton = ({ icon, label, onClick, flex }) => {
  const theme = useTheme();
  return (
    <EGButton
      size="thin"
      label={label ?? "Button"}
      iconName={icon ?? "outline_edit_form"}
      iconColor="secondary"
      iconColorHover="white"
      iconSize={18}
      onClick={typeof onClick === "function" ? onClick : () => {}}
      sx={{
        flex,
        transition: "none",
        paddingX: 0.8,
        maxWidth: "fit-content", // Se expande para mostrar el texto
        whiteSpace: "nowrap", // Evita que el texto se divida en varias líneas
        "&:hover": {
          background: `linear-gradient(180deg, ${
            theme.palette.ethicsBlue.main
          } 0%, ${darken(theme.palette.ethicsBlue.main, 0.2)} 100%)`,
          border: `1px solid transparent`,
          color: theme.palette.white.main,
        },
      }}
    />
  );
};

const HeaderIconButton = ({ tooltip, icon, onClick }) => {
  const theme = useTheme();
  return (
    <Tooltip arrow title={tooltip ?? ""}>
      <span>
        <EGButton
          size="thin"
          iconName={icon ?? "outline_edit_form"}
          variant="contained"
          iconColor="secondary"
          iconColorHover="white"
          iconSize={18}
          sx={{
            transition: "none",
            padding: 0,
            minWidth: 30,
            maxWidth: 30,
            "&:hover": {
              background: `linear-gradient(180deg, ${
                theme.palette.ethicsBlue.main
              } 0%, ${darken(theme.palette.ethicsBlue.main, 0.2)} 100%)`,
              border: `1px solid transparent`,
              color: theme.palette.white.main,
            },
          }}
          onClick={typeof onClick === "function" ? onClick : () => {}}
        />
      </span>
    </Tooltip>
  );
};

const ShowOnScroll = ({ trigger, rHDimensions, children }) => {
  return (
    <Slide
      appear={true}
      direction="down"
      in={trigger}
      timeout={{ enter: 300, exit: 300 }} //
      sx={{
        boxShadow: "5px 5px 5px 0px rgba(0, 0, 0, 0.2)",
        position: "fixed",
        top: 0,
        right: 0,
        width: rHDimensions.width,
        zIndex: 10,
        transition: "opacity 0.3s ease-in-out",
        opacity: trigger ? 1 : 0,
      }}
    >
      {children ?? <div />}
    </Slide>
  );
};

export const ReportHeader2 = ({
  reportDetail,
  assignedUsers,
  showIsAnonymous = true,
  showAssignedUsers = true,
  showTrackingCode = true,
  showIndicators = true,
  buttons = [],
}) => {
  // Custom Hooks
  const { email } = useSelector((state) => state.auth);
  const { t } = useTranslation("report");
  const theme = useTheme();
  const { contentRect } = useSelector((state) => state.theme);
  const { enqueueSnackbar } = useSnackbar();
  const { entryways, icon_indicators, status_locales, evaluated_facts } =
    useReportContext();
  const getLocalizedLabel = useLocalizedLabel();
  // Initial States
  const [trigger, setTrigger] = useState(false);
  const [rHDimensions, setRHDimensions] = useState({
    width: "100%",
    height: "auto",
  });
  const reportHeaderRef = useRef(null);
  const [isCopy, setIsCopy] = useState(false);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  // Gets indicatorTags base
  useEffect(() => {
    const fetchData = async () => {
      try {
        const resp = await catalogByPart({
          is_own: false,
          catalogue: "TG-100",
          path: "/",
        });
        if (resp.error) {
          console.error("Error in API response:", resp);
          return;
        }
        setTags(resp.data);
      } catch (err) {
        console.error(err);
      }
    };

    fetchData();
  }, []);

  // Hydration of indicatorTags
  useEffect(() => {
    if (reportDetail.tags) {
      const selectedKeys = reportDetail.tags;
      setSelectedTags(selectedKeys);
    }
  }, [reportDetail]);

  // Loops through the array of tag objects and builds a new
  const transformedType = useMemo(() => {
    return tags.reduce((acc, tag) => {
      acc[tag.key] = tag;
      return acc;
    }, {});
  }, [tags]);

  const returnButton = (button, i) => {
    const flex = "0 1 200px";
    if (button.type === "button")
      return <HeaderButton flex={flex} {...button} key={`button-${i}`} />;
    if (button.type === "icon-button")
      return <HeaderIconButton flex={flex} {...button} key={`button-${i}`} />;
    if (button.type === "customIndicators" && tags.length > 0)
      return (
        <IndicatorTags
          flex={flex}
          key={`button-${i}`}
          tags={tags}
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
          transformedType={transformedType}
          {...button}
        />
      );
    return null;
  };

  // Copy text to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(reportDetail.tracking_code);
      setIsCopy(true);
      enqueueSnackbar(t("ReportHeader.copied_to_clipboard"), {
        variant: "info",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        preventDuplicate: true,
        autoHideDuration: 2000,
      });
      setTimeout(() => {
        setIsCopy(false);
      }, 2000);
    } catch (error) {
      console.error("could not copy");
    }
  };

  // Rendering of the icons corresponding to the indicators
  const PresentIndicators = ({ info }) => {
    const indicators = info?.indicators;
    const facts = info?.resolution?.facts_verified;

    return (
      <Stack
        display="flex"
        alignItems="center"
        justifyContent={{ xs: "start", md: "end" }}
        flexDirection="row"
        flexWrap="wrap"
        spacing={0.5}
      >
        {Object.entries(indicators).map(([key, value], i) => {
          if (value && icon_indicators[key]) {
            const {
              text,
              iconName,
              iconColor = "secondary",
              iconColorHover,
              btnColorHover,
            } = icon_indicators[key];
            return (
              <EGIconButton
                key={`${key}-${i}`}
                colorHover={btnColorHover || "transparent"}
                iconName={iconName}
                title={text}
                iconColor={iconColor}
                iconColorHover={iconColorHover || "primary"}
                iconSize={18}
                sx={{ paddingY: 0 }}
              />
            );
          }
        })}
        {facts && (
          <EGIconButton
            colorHover="transparent"
            iconName={evaluated_facts[facts].iconName}
            title={evaluated_facts[facts].label}
            iconColor={evaluated_facts[facts].color}
            iconColorHover={"primary"}
            iconSize={18}
            sx={{ paddingY: 0 }}
          />
        )}
        <TagsPreview
          tags={selectedTags}
          transformedType={transformedType}
          getLocalizedLabel={getLocalizedLabel}
          tooltipTitle={t("ReportHeader.classification_tags")}
          iconSize={20}
        />
      </Stack>
    );
  };

  // Get dimensions of ReportHeader
  const updateDimensions = () => {
    setRHDimensions({
      width: contentRect.width,
      height: contentRect.height,
    });
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setTrigger(!entry.isIntersecting);
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.3,
      },
    );

    if (reportHeaderRef.current) {
      observer.observe(reportHeaderRef.current);
      updateDimensions();
    }

    return () => {
      if (reportHeaderRef.current) {
        observer.unobserve(reportHeaderRef.current);
      }
    };
  }, []);

  useEffect(() => {
    updateDimensions();
  }, [contentRect]);

  // Simplified reporting information
  const entryway = entryways[reportDetail?.entry_way].iconName;
  const entrywayLabel = entryways[reportDetail?.entry_way].label;
  // Estatus mostrado en el header. Igual que en ReportsList, cuando el reporte
  // está "closed" distinguimos el subtipo de cierre:
  // - status.details.closed => founded/unfounded/undefined (ya cerrado)
  // - sin details => aún en proceso de cierre (color/label de "closing_process")
  const currentStatus = reportDetail?.status?.current;
  let color = status_locales[currentStatus].color || "draft";
  let status = status_locales[currentStatus].label || t("ReportHeader.draft");
  if (currentStatus === "closed") {
    const closedKind = reportDetail?.status?.details?.closed;
    const closedLabels = {
      founded: t("ReportsList.closed_founded"),
      unfounded: t("ReportsList.closed_unfounded"),
      undefined: t("ReportsList.closed_undetermined"),
    };
    if (closedKind && closedLabels[closedKind]) {
      status = closedLabels[closedKind];
    } else {
      status = status_locales["closing_process"].label;
      color = status_locales["closing_process"].color;
    }
  }
  const trackingCode = reportDetail.tracking_code.replace(
    /(\d{3})(?=\d)/g,
    "$1 ",
  );

  const numberOfInfoRight = () => {
    let count = 0;
    showTrackingCode && count++;
    showIndicators && count++;
    buttons.length > 0 && count++;
    return count;
  };

  return (
    <>
      {/* Initial menu */}
      <Grid2
        ref={reportHeaderRef}
        className="dyTheme1 dyBorder1 rounded mt-20"
        container
        py={1}
        px={2}
      >
        <Grid2 size={{ xs: 12, md: 6 }} sx={{ my: "auto" }}>
          {showIsAnonymous && (
            <ListItem dense sx={{ p: 0, mb: 1 }}>
              <ListItemIcon sx={{ mx: "auto", minWidth: "45px" }}>
                <Tooltip title={entrywayLabel} arrow>
                  <Paper
                    sx={{
                      background: `${alpha(color, 0.85)}`,
                      width: 35,
                      height: 35,
                      display: "grid",
                      placeContent: "center",
                    }}
                    className="rounded"
                    elevation={0}
                  >
                    <Icon
                      name={entryway}
                      color={theme.palette.common.white}
                      size={20}
                    />
                  </Paper>
                </Tooltip>
              </ListItemIcon>
              <ListItemText
                slotProps={{
                  primary: { fontSize: 15, fontWeight: 600 },
                  secondary: { fontSize: 12 },
                }}
                sx={{ marginY: 0 }}
                primary={
                  reportDetail?.indicators?.is_anonymous
                    ? t("ReportViewer.anonymous")
                    : t("ReportViewer.no_anonymous")
                }
                secondary={
                  <>
                    {`${t("ReportHeader.updated_at")}: ${
                      parseDateToLgComun4(
                        reportDetail?.updated_at,
                        undefined,
                        true
                      ) || t("ReportHeader.no_updates")
                    }`}
                    {reportDetail?.updated_at && (
                      <UTCHint date={reportDetail.updated_at} />
                    )}
                  </>
                }
              />
            </ListItem>
          )}

          <Stack
            display="flex"
            alignItems="center"
            flexDirection="row"
            flexWrap="wrap"
            spacing={1.5}
          >
            <Typography
              className="rounded"
              sx={{
                background: `${alpha(color, 0.2)}`,
                px: 3,
                py: 0.5,
                border: `2px solid ${color}`,
              }}
              fontSize={12}
              elevation={0}
            >
              {status}
            </Typography>

            {showAssignedUsers && assignedUsers.length > 0 && (
              <Stack
                display="flex"
                alignItems="center"
                justifyContent="space-evenly"
                flexDirection="row"
                flexWrap="wrap"
                columnGap={1}
              >
                <AvatarGroup contacts={assignedUsers} max={3} />
                <Typography variant="body2" color="secondary" fontSize={12}>
                  {`${
                    assignedUsers?.find((item) => item.email === email)?.name ||
                    "--"
                  } ${
                    assignedUsers.length > 1 &&
                    `${t("ReportHeader.and")} ${assignedUsers.length - 1}
              ${t("ReportHeader.others")}`
                  }`}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Grid2>

        <Grid2
          size={{ xs: 12, md: 6 }}
          display="flex"
          marginTop={{ xs: 1, md: 0 }}
          justifyContent={numberOfInfoRight() > 1 ? "space-between" : "center"}
          flexDirection="column"
          rowGap={1}
        >
          {showTrackingCode && (
            <Typography
              variant="body2"
              textAlign={{ md: "end" }}
              fontSize={13}
              fontWeight={600}
              marginBottom={-0.4}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                flexFlow: "row wrap",
              }}
            >
              {`${t("ReportHeader.tracking_code")}:`}
              <Typography
                component="span"
                fontSize={16}
                ml={1}
                fontWeight="bold"
                lineHeight={0.5}
                color={theme.palette.primary.main}
                sx={{ whiteSpace: "nowrap" }}
              >
                {trackingCode}
              </Typography>
              <EGIconButton
                sx={{ ml: 0.25 }}
                iconSize={17}
                title={t(`ReportHeader.${isCopy ? "copied" : "copy"}`)}
                onClick={handleCopy}
                iconName={
                  isCopy ? "outline_copy_succes" : "outline_document_copy"
                }
                iconColor="primary"
              />
            </Typography>
          )}

          {showIndicators && <PresentIndicators info={reportDetail} />}

          {buttons.length > 0 && (
            <Box
              display="flex"
              flexDirection="row"
              flexWrap="wrap"
              alignItems="center"
              justifyContent="flex-end"
              gap={1}
            >
              {buttons.map((button, i) => returnButton(button, i))}
            </Box>
          )}
        </Grid2>
      </Grid2>

      {/* Menu sticky */}
      <ShowOnScroll trigger={trigger} rHDimensions={rHDimensions}>
        <Stack
          display="flex"
          alignItems="center"
          justifyContent={"flex-end"}
          flexDirection="row"
          flexWrap="wrap"
          width="100%"
          className="dyTheme1"
          py={1}
          px={2}
          mt={5.5}
          spacing={1}
        >
          <Stack
            spacing={3}
            display="flex"
            alignItems="center"
            flexDirection="row"
          >
            <Tooltip arrow title={t("searchBar.report_status")}>
              <Typography
                className="rounded"
                sx={{
                  background: `${alpha(color, 0.2)}`,
                  px: 5,
                  py: 0.25,
                  border: `1px solid ${color}`,
                  display: { xs: "none", sm: "block" },
                }}
                elevation={0}
              >
                {status}
              </Typography>
            </Tooltip>
          </Stack>

          <Box
            display="flex"
            flexDirection="row"
            flexWrap="wrap"
            alignItems="flex-end"
            gap={1}
          >
            {buttons.map((button, i) => returnButton(button, i))}
          </Box>
        </Stack>
      </ShowOnScroll>
    </>
  );
};
